import { useEffect, useState } from "react";
import { useReservationContext } from "../context";
import { getMyReservationById, getReservationById } from "../services";

export type SelectedReservationData = {
  id?: string;
  title?: string;
  eventType?: number;
  eventStartTime?: string;
  eventEndTime?: string;
  deviceInternalId?: string;
  deviceTypeName?: string;
  providerId?: string;
  [key: string]: any;
};

const reservationCache = new Map<string, SelectedReservationData>();

export function useSelectedReservation(isAdmin: boolean): {
  reservation: SelectedReservationData | null;
  loading: boolean;
} {
  const { reservationId } = useReservationContext();

  const [reservation, setReservation] = useState<SelectedReservationData | null>(
    () => (reservationId ? (reservationCache.get(reservationId) ?? null) : null)
  );
  const [loading, setLoading] = useState<boolean>(
    () => Boolean(reservationId && !reservationCache.has(reservationId))
  );

  useEffect(() => {
    if (!reservationId) {
      setReservation(null);
      setLoading(false);
      return;
    }

    const cached = reservationCache.get(reservationId);
    if (cached) {
      setReservation(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fetchFn = isAdmin ? getReservationById : getMyReservationById;
    fetchFn(reservationId).then((resp) => {
      if (cancelled) return;
      if (resp) {
        reservationCache.set(reservationId, resp);
        setReservation(resp);
      }
      setLoading(false);
    }).catch((err) => {
      if (cancelled) return;
      console.error("[useSelectedReservation] Failed to fetch reservation:", err);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [reservationId, isAdmin]);

  return { reservation, loading };
}
